import { calculate } from '@calculations/price/strategies/fixed-customer/calculate';
import { calculateMarginsFromUnitPrice } from '@calculations/price/utilities/margins';
import { SnapRoundingOptionsCustom } from '@calculations/round';

import {
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  RecalculatePriceStrategy,
} from '../types';
import { getAllowFallback } from '../utilities/get-allow-fallback';
import { noOpUnitPrice } from './no-op';

function onQuantityChange(
  inputs: OnQuantityChangeInput,
): OnQuantityChangeOutput {
  const { state, quoteLine, quotePrice, quantity, priceStrategyConfigs } =
    inputs;

  if (!quoteLine.custPriceEffects) {
    return {
      status: 'failure',
      reason: 'No customer price effects found',
    };
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    priceStrategyConfigs,
  );

  const output = calculate(
    {
      quantity,
      allowFallback,
      custPriceEffects: quoteLine.custPriceEffects,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  const margins = calculateMarginsFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitVariableCosts: quotePrice.unitVariableCosts,
    unitPrice: output.unitPrice,
  });

  return {
    status: 'success',
    unitPrice: output.unitPrice,
    ...margins,
  };
}

function onQuotePriceCreation(
  inputs: OnQuotePriceCreationInput,
): OnQuotePriceCreationOutput {
  const { state, quoteLine, quotePrice, priceStrategyConfigs } = inputs;
  if (!quoteLine.custPriceEffects) {
    return {
      status: 'failure',
      reason: 'No customer price effects found',
    };
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    priceStrategyConfigs,
  );

  const output = calculate(
    {
      quantity: quotePrice.quantity,
      allowFallback,
      custPriceEffects: quoteLine.custPriceEffects,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  const margins = calculateMarginsFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitVariableCosts: quotePrice.unitVariableCosts,
    unitPrice: output.unitPrice,
  });

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice: output.unitPrice,
      grossMargin: margins.grossMargin ?? null,
      contributionMargin: margins.contributionMargin ?? null,
    },
  };
}

export const recalculateFixedCustomer = {
  onQuantityChange,
  onUnitPriceChange: noOpUnitPrice,
  onGrossMarginChange: null,
  onContributionMarginChange: null,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
